import React from "react";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import { Phone, PhoneOff, Mic, MicOff, X as XIcon, PhoneIncoming, PhoneOutgoing } from "lucide-react";
import { useCall } from "../lib/call-context";

export function ActiveCallIndicator() {
  const {
    call,
    incomingCall,
    connectionStatus,
    acceptCall,
    rejectCall,
    hangupCall,
    activeCallLabel,
    incomingCallLabel,
    hasMicrophone,
    microphonePermission,
    requestMicrophonePermission,
    muted,
    toggleMute,
  } = useCall();

  const micReady = hasMicrophone && microphonePermission === "granted";
  const canRequestMic = !micReady && microphonePermission !== "denied";
  const micBlocked = microphonePermission === "denied";

  const isConnectedCall = !!call && connectionStatus === "connected";
  const isOutboundDialing = !incomingCall && connectionStatus === "connecting";
  const isInCallUi = !!incomingCall || !!call || isOutboundDialing;

  // Tooltip z-index fix
  const tooltipSlotProps: any = {
    root: { sx: { zIndex: 99999 } },
    popper: { sx: { zIndex: 99999 } },
  };

  // --- INCOMING CALL UI ---
  if (incomingCall) {
    const acceptColor = microphonePermission === "granted" ? "success" : "warning";
    const acceptTitle =
      !hasMicrophone
        ? "No microphone found"
        : microphonePermission === "granted"
          ? "Accept call"
          : "Click to allow mic & accept";

    return (
      <Tooltip
        title={incomingCallLabel || incomingCall?.parameters.From || "Unknown"}
        slotProps={tooltipSlotProps}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "danger.softBg",
            borderRadius: "xl",
            px: 1.5,
            py: 0.5,
            animation: "pulse-ring 1.5s ease-out infinite",
            "@keyframes pulse-ring": {
              "0%": { boxShadow: "0 0 0 0 rgba(220, 38, 38, 0.4)" },
              "70%": { boxShadow: "0 0 0 6px rgba(220, 38, 38, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(220, 38, 38, 0)" },
            },
          }}
        >
          <PhoneIncoming size={14} style={{ color: "var(--joy-palette-danger-600)" }} />
          <Typography level="body-xs" sx={{ fontWeight: 700, color: "danger.700" }}>
            Incoming
          </Typography>
          <Typography
            level="body-xs"
            sx={{
              color: "danger.600",
              maxWidth: 140,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {incomingCallLabel || incomingCall?.parameters.From || "Unknown"}
          </Typography>

          <Tooltip title={acceptTitle} slotProps={tooltipSlotProps}>
            <span>
              <IconButton
                size="sm"
                variant="solid"
                color={acceptColor}
                onClick={acceptCall}
                disabled={!hasMicrophone}
                sx={{ ml: 0.5 }}
              >
                <Phone size={14} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Decline" slotProps={tooltipSlotProps}>
            <IconButton size="sm" variant="solid" color="danger" onClick={rejectCall}>
              <XIcon size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      </Tooltip>
    );
  }

  // --- ACTIVE CALL / DIALING UI ---
  if (call || isOutboundDialing) {
    const callLabel = activeCallLabel || call?.parameters.To || "Unknown";
    const statusText = isConnectedCall ? "On Call" : "Calling";
    const statusSub = isConnectedCall ? null : (connectionStatus === "connecting" ? "ringing…" : connectionStatus);

    return (
      <Tooltip title={callLabel} slotProps={tooltipSlotProps}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: isConnectedCall ? "success.softBg" : "warning.softBg",
            borderRadius: "xl",
            px: 1.5,
            py: 0.5,
          }}
        >
          {isConnectedCall ? (
            <Phone size={14} style={{ color: "var(--joy-palette-success-600)" }} />
          ) : (
            <PhoneOutgoing size={14} style={{ color: "var(--joy-palette-warning-600)" }} />
          )}
          <Typography
            level="body-xs"
            sx={{ fontWeight: 700, color: isConnectedCall ? "success.700" : "warning.700" }}
          >
            {statusText}
          </Typography>
          <Typography
            level="body-xs"
            sx={{
              color: isConnectedCall ? "success.600" : "warning.600",
              maxWidth: 140,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {callLabel}
          </Typography>
          {statusSub ? (
            <Typography level="body-xs" sx={{ color: "warning.700", opacity: 0.85 }}>
              {statusSub}
            </Typography>
          ) : null}

          {isConnectedCall && (
            <Tooltip title={muted ? "Unmute" : "Mute"} slotProps={tooltipSlotProps}>
              <IconButton
                size="sm"
                variant={muted ? "solid" : "soft"}
                color={muted ? "warning" : "neutral"}
                onClick={toggleMute}
                sx={{ ml: 0.5 }}
              >
                {muted ? <MicOff size={14} /> : <Mic size={14} />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isConnectedCall ? "End call" : "Cancel"} slotProps={tooltipSlotProps}>
            <IconButton size="sm" variant="solid" color="danger" onClick={hangupCall}>
              <PhoneOff size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      </Tooltip>
    );
  }

  // --- IDLE STATE ---
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {micReady ? (
        <Chip
          size="md"
          variant="soft"
          color="success"
          startDecorator={<Mic size={14} />}
          sx={{ fontWeight: 500, px: 1.5 }}
        >
          Microphone Ready
        </Chip>
      ) : micBlocked ? (
        <Chip
          size="md"
          variant="soft"
          color="danger"
          startDecorator={<MicOff size={14} />}
          sx={{ fontWeight: 500, px: 1.5 }}
        >
          Microphone Blocked
        </Chip>
      ) : !hasMicrophone ? (
        <Tooltip title="No microphone detected" slotProps={tooltipSlotProps}>
          <Chip
            size="md"
            variant="soft"
            color="warning"
            startDecorator={<MicOff size={14} />}
            sx={{ fontWeight: 500, px: 1.5 }}
          >
            No Microphone
          </Chip>
        </Tooltip>
      ) : canRequestMic ? (
        <Tooltip title="Click to allow microphone" slotProps={tooltipSlotProps}>
          <Button
            size="sm"
            variant="solid"
            color="danger"
            startDecorator={<MicOff size={14} />}
            onClick={requestMicrophonePermission}
            sx={{
              borderRadius: "999px",
              fontWeight: 800,
              px: 2,
              py: 0.6,
              textTransform: "none",
              boxShadow: "sm",
              animation: "pulse-cta 1.4s ease-out infinite",
              "@keyframes pulse-cta": {
                "0%": { boxShadow: "0 0 0 0 rgba(220,38,38,0.45)" },
                "70%": { boxShadow: "0 0 0 8px rgba(220,38,38,0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(220,38,38,0)" },
              },
            }}
          >
            Allow Microphone
          </Button>
        </Tooltip>
      ) : (
        <Chip
          size="md"
          variant="soft"
          color="warning"
          startDecorator={<MicOff size={14} />}
          sx={{ fontWeight: 700, px: 1.5 }}
        >
          Mic Needed
        </Chip>
      )}
    </Box>
  );
}
